import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface ManagerDashboardData {
  metrics: {
    revenue: number;
    revenueChange: number;
    efficiency: number;
    efficiencyChange: number;
    completedJobs: number;
    jobsChange: number;
    avgServiceTime: number;
    serviceTimeChange: number;
    customerSatisfaction: number;
    satisfactionChange: number;
    staffUtilization: number;
    utilizationChange: number;
  };
  alerts: Array<{
    id: string;
    type: 'warning' | 'error' | 'info' | 'success';
    title: string;
    message: string;
    timestamp: Date;
  }>;
  recentJobs: any[];
  performanceData: any;
  insights: any[];
}

export interface Employee {
  id: string;
  name: string;
  role: string;
  regularHours: number;
  overtimeHours: number;
  totalHours?: number;
  hourlyRate: number;
  schedule?: Array<{
    day: string;
    hours: number;
  }>;
}

export interface PayrollData {
  weekStart: string;
  weekEnd: string;
  employees: Employee[];
  totals: {
    regularHours: number;
    overtimeHours: number;
    totalHours: number;
    regularPay: number;
    overtimePay: number;
    totalPay: number;
  };
}

class ManagerService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getDashboardData(): Promise<ManagerDashboardData> {
    try {
      const response = await axios.get(`${API_BASE_URL}/manager/dashboard`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching manager dashboard data:', error);
      // Return mock data for development
      return this.getMockDashboardData();
    }
  }

  async getPayrollData(weekStart: string): Promise<PayrollData> {
    try {
      const response = await axios.get(`${API_BASE_URL}/manager/payroll`, {
        params: { weekStart },
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      return this.getMockPayrollData(weekStart);
    }
  }

  async submitPayroll(data: PayrollData): Promise<void> {
    const response = await axios.post(`${API_BASE_URL}/manager/payroll/submit`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getPayrollAISuggestions(data: any): Promise<any> {
    try {
      const response = await axios.post(`${API_BASE_URL}/manager/payroll/ai-assist`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      return {
        suggestions: [
          {
            employeeId: data.employees[0]?.id,
            suggestion: 'Based on historical data, this employee typically works 40 regular hours',
            confidence: 0.92
          }
        ],
        insights: [
          'Total hours are 5% higher than the 4-week average',
          'Consider scheduling optimization for next week'
        ]
      };
    }
  }

  async getKPIData(kpiType: string, dateRange: any): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/manager/kpi/${kpiType}`, {
      params: dateRange,
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async exportReport(type: string, data: any): Promise<Blob> {
    const response = await axios.post(
      `${API_BASE_URL}/manager/export`,
      { type, data },
      {
        headers: this.getAuthHeaders(),
        responseType: 'blob',
      }
    );
    return response.data;
  }

  private getMockDashboardData(): ManagerDashboardData {
    return {
      metrics: {
        revenue: 42500,
        revenueChange: 8.5,
        efficiency: 92.3,
        efficiencyChange: 2.1,
        completedJobs: 234,
        jobsChange: 12.0,
        avgServiceTime: 45,
        serviceTimeChange: -5.2,
        customerSatisfaction: 94.5,
        satisfactionChange: 1.2,
        staffUtilization: 87.5,
        utilizationChange: 3.4
      },
      alerts: [
        {
          id: '1',
          type: 'warning',
          title: 'Low Inventory Alert',
          message: 'Oil filters running low (12 units remaining)',
          timestamp: new Date()
        }
      ],
      recentJobs: [],
      performanceData: {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        datasets: [
          {
            label: 'Jobs Completed',
            data: [32, 38, 35, 42, 45, 52]
          }
        ]
      },
      insights: []
    };
  }

  private getMockPayrollData(weekStart: string): PayrollData {
    const employees: Employee[] = [
      {
        id: '1',
        name: 'John Smith',
        role: 'Senior Technician',
        regularHours: 40,
        overtimeHours: 5,
        hourlyRate: 28
      },
      {
        id: '2',
        name: 'Mike Johnson',
        role: 'Technician',
        regularHours: 38,
        overtimeHours: 0,
        hourlyRate: 22
      },
      {
        id: '3',
        name: 'Sarah Williams',
        role: 'Service Advisor',
        regularHours: 40,
        overtimeHours: 2,
        hourlyRate: 20
      },
      {
        id: '4',
        name: 'Tom Davis',
        role: 'Junior Technician',
        regularHours: 35,
        overtimeHours: 0,
        hourlyRate: 18
      }
    ];

    // Calculate totals
    const totals = employees.reduce((acc, emp) => {
      acc.regularHours += emp.regularHours;
      acc.overtimeHours += emp.overtimeHours;
      acc.totalHours += emp.regularHours + emp.overtimeHours;
      acc.regularPay += emp.regularHours * emp.hourlyRate;
      acc.overtimePay += emp.overtimeHours * emp.hourlyRate * 1.5;
      acc.totalPay += (emp.regularHours * emp.hourlyRate) + (emp.overtimeHours * emp.hourlyRate * 1.5);
      return acc;
    }, {
      regularHours: 0,
      overtimeHours: 0,
      totalHours: 0,
      regularPay: 0,
      overtimePay: 0,
      totalPay: 0
    });

    return {
      weekStart,
      weekEnd: new Date(new Date(weekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      employees: employees.map(emp => ({
        ...emp,
        totalHours: emp.regularHours + emp.overtimeHours
      })),
      totals
    };
  }
}

export const managerService = new ManagerService();